import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import Button from "../common/Button";
import Input from "../common/Input";
import Loading from "../common/Loading";
import bgImg from "../../pnai-bg.jpg";
import Logo from "../common/Logo";

const AcceptTransfer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agencyName, setAgencyName] = useState<string>("");
  const [newOwnerEmail, setNewOwnerEmail] = useState<string>("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [errorState, setErrorState] = useState<boolean>(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid transfer invitation link");
      setIsLoading(false);
      return;
    }

    // Validate the token and get agency information
    const validateToken = async () => {
      try {
        const response = await axios.get(`/api/agency/validate-transfer-token?token=${token}`);
        setAgencyName(response.data.agencyName);
        setNewOwnerEmail(response.data.newOwnerEmail);
        setIsLoading(false);
      } catch (error: any) {
        console.error("Error validating token:", error);
        setError(error.response?.data?.error || "Invalid transfer invitation link");
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const validateForm = () => {
    let newErrors: {
      newPassword?: string;
      confirmPassword?: string;
    } = {};

    if (!newPassword) {
      newErrors.newPassword = "Password is required";
    } else if (newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters long";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      setErrorState(false);

      try {
        await axios.post("/api/agency/accept-transfer", {
          token,
          newPassword,
        });

        toast.success("Agency transfer completed successfully! Redirecting to login...");

        // Keep spinner showing during redirect
        setTimeout(() => {
          navigate("/agency/login");
        }, 2000);
      } catch (error: any) {
        console.error("Error accepting transfer:", error);
        setErrorState(true);
        setErrors({
          overall:
            error.response?.data?.error || "Failed to accept transfer invitation",
        });
        setIsSubmitting(false);
      }
    } else {
      setErrorState(true);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div
          className="hidden md:flex relative items-center justify-center bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${bgImg})` }}
        >
          <div className="absolute inset-0 bg-blue-50/70 dark:bg-blue-900/70"></div>
          <div className="w-3/5 z-10">
            <Logo className="w-full h-auto" />
          </div>
        </div>
        <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-base-200">
          <div className="bg-base-100 p-8 rounded-xl shadow-md dark:border dark:border-base-300 w-full max-w-sm">
            <h1 className="text-3xl mb-4 font-semibold text-red-600 dark:text-red-400">
              Invalid Invitation
            </h1>
            <p className="text-gray-700 dark:text-base-content/80 mb-6">{error}</p>
            <Button
              onClick={() => navigate("/agency/login")}
              variant="primary"
              className="w-full"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2">
      <div
        className="hidden md:flex relative items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImg})` }}
      >
        <div className="absolute inset-0 bg-blue-50/70 dark:bg-blue-900/70"></div>
        <div className="w-3/5 z-10">
          <Logo className="w-full h-auto" />
        </div>
      </div>
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-base-200">
        <div className="bg-base-100 p-8 rounded-xl shadow-md dark:border dark:border-base-300 w-full max-w-sm">
          <h1 className="text-2xl mb-4 font-semibold">Accept Agency Transfer</h1>
          <p className="mb-6 text-sm text-gray-600 dark:text-base-content/70">
            Create a password to become the new agency administrator of <span className="font-semibold text-base-content">{agencyName}</span>
          </p>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <Input
                title="Email"
                type="email"
                id="email"
                value={newOwnerEmail}
                onChange={() => {}} // No-op since it's disabled
                disabled
                className="w-full"
              />
            </div>

            <div>
              <Input
                title="New Password"
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setNewPassword(e.target.value);
                }}
                placeholder="Enter your new password (min 8 characters)"
                className={`w-full ${errorState && "border-red-500"}`}
              />
              {errors.newPassword && errorState && (
                <p className="mt-1 text-xs text-red-500">{errors.newPassword}</p>
              )}
            </div>

            <div>
              <Input
                title="Confirm Password"
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setConfirmPassword(e.target.value);
                }}
                placeholder="Confirm your new password"
                className={`w-full ${errorState && "border-red-500"}`}
              />
              {errors.confirmPassword && errorState && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.confirmPassword}
                </p>
              )}
              {errors.overall && errorState && (
                <p className="mt-1 text-xs text-red-500">{errors.overall}</p>
              )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-md p-4">
              <p className="text-xs text-blue-800 dark:text-blue-300 font-semibold">
                What happens next:
              </p>
              <ul className="mt-2 text-xs text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                <li>You will become the primary administrator</li>
                <li>You'll have full access to agency settings</li>
                <li>The previous owner will lose admin access</li>
              </ul>
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Processing...
                </>
              ) : (
                "Accept Transfer & Create Account"
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AcceptTransfer;
